/**
 * =============================================================================
 * Página: AffiliateSignupResolver
 * Versão: 1.0.0
 * Rotas: /afiliado/cadastro          (legado/global)
 *        /afiliado/:tenantSlug/cadastro  (canônico por tenant)
 *
 * Descrição:
 *   Resolve o programa de indicação PADRÃO do tenant e redireciona o
 *   visitante para a landing real de cadastro:
 *     /afiliado/cadastro/<tenantSlug>/<programSlug>
 *
 *   - Se o `tenantSlug` não vier na URL, tenta o último em cache (helper
 *     `getLastAffiliateTenantSlug`). Se ainda assim não houver, mostra
 *     mensagem amigável pedindo o link enviado pelo administrador.
 *   - Se o tenant existir mas não tiver programa padrão configurado,
 *     mostra estado de erro com CTA para voltar ao login.
 * =============================================================================
 */
import { useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  affiliateLoginPath,
  getLastAffiliateTenantSlug,
} from '@/lib/affiliate-urls';
import { useDefaultAffiliateProgram } from '@/hooks/useDefaultAffiliateProgram';

export default function AffiliateSignupResolver() {
  const { tenantSlug: paramSlug } = useParams<{ tenantSlug?: string }>();
  const navigate = useNavigate();

  // Tenant: prioriza o da rota; fallback no cache local (último visitado).
  const tenantSlug = (paramSlug ?? getLastAffiliateTenantSlug() ?? '').toLowerCase();

  const { data, isLoading, isError, error } = useDefaultAffiliateProgram(
    tenantSlug || null,
  );

  // Quando o programa padrão é resolvido, redireciona para a landing real.
  useEffect(() => {
    if (!data) return;
    navigate(
      `/afiliado/cadastro/${data.tenant_slug}/${data.program_slug}`,
      { replace: true },
    );
  }, [data, navigate]);

  // ---------------------------------------------------------------------------
  // Estado: nenhum tenant identificado
  // ---------------------------------------------------------------------------
  if (!tenantSlug) {
    return (
      <CenteredCard
        icon={<AlertCircle className="h-12 w-12 text-destructive mx-auto mb-2" />}
        title="Tenant não identificado"
        description="Use o link de cadastro enviado pelo administrador da empresa para a qual deseja se afiliar."
        ctaTo="/afiliado/login"
        ctaLabel="Ir para login"
      />
    );
  }

  // ---------------------------------------------------------------------------
  // Estado: carregando programa padrão
  // ---------------------------------------------------------------------------
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm">Carregando programa de indicação...</p>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Estado: tenant existe mas sem programa padrão configurado
  // ---------------------------------------------------------------------------
  if (!data || isError) {
    return (
      <CenteredCard
        icon={<AlertCircle className="h-12 w-12 text-destructive mx-auto mb-2" />}
        title="Cadastro indisponível"
        description={
          isError && (error as any)?.message
            ? 'Não foi possível carregar o programa de indicação no momento.'
            : 'Esta empresa ainda não definiu um programa de indicação padrão. Entre em contato com o administrador.'
        }
        ctaTo={affiliateLoginPath(tenantSlug)}
        ctaLabel="Voltar ao login"
      />
    );
  }

  // Caso "data" venha pronto, o useEffect já redirecionou — fallback visual:
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente auxiliar — cartão centralizado para estados terminais
// ---------------------------------------------------------------------------
interface CenteredCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  ctaTo: string;
  ctaLabel: string;
}

function CenteredCard({
  icon,
  title,
  description,
  ctaTo,
  ctaLabel,
}: CenteredCardProps) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {icon}
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild className="w-full" variant="outline">
            <Link to={ctaTo}>{ctaLabel}</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
